import { stripeCreateSubscription } from './api';

export const LT_PLUS_BILLING_INTERVAL = 'monthly';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const DEFAULT_MEMBERSHIP_POLL_ATTEMPTS = 8;
const DEFAULT_MEMBERSHIP_POLL_DELAY_MS = 750;

/**
 * After LT+ checkout, refreshes the user profile once. Does not poll for webhook sync —
 * server-side pricing uses Stripe as a fallback for GLP waiver when metadata lags.
 */
export const ensureMembershipForLtPlusCheckout = async fetchUser => fetchUser();

export const isLtPlusSubscriptionExistsError = error => {
  if (!error) {
    return false;
  }

  const message = String(error.message || error).toLowerCase();
  const code = String(error.code || '').toLowerCase();

  return (
    code.includes('subscription_exists') ||
    code.includes('stripe-subscription-exists') ||
    message.includes('already have an active landtrust+ subscription') ||
    message.includes('subscription already exists')
  );
};

export const parseSubscriptionApiError = errorText => {
  try {
    const parsed = JSON.parse(errorText);
    const code = parsed.code || parsed.data?.errors?.[0]?.code;
    const err = new Error(parsed.message || errorText);

    if (code) {
      err.code = code;
    }

    return err;
  } catch {
    return new Error(errorText);
  }
};

const waitForPremiumMembershipWithOptions = async (fetchUser, options = {}) => {
  const { maxAttempts, delayMs } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const user = await fetchUser();
    const tier = user?.attributes?.profile?.metadata?.membershipTier;

    if (tier === 'premium' || tier === 'admin') {
      return user;
    }

    if (attempt < maxAttempts - 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(delayMs);
    }
  }

  return null;
};

/**
 * Polls for premium membership after subscription payment (webhook may lag).
 */
export const waitForPremiumMembership = async (fetchUser, options = {}) => {
  const {
    maxAttempts = DEFAULT_MEMBERSHIP_POLL_ATTEMPTS,
    delayMs = DEFAULT_MEMBERSHIP_POLL_DELAY_MS,
  } = options;

  const user = await waitForPremiumMembershipWithOptions(fetchUser, { maxAttempts, delayMs });

  if (user) {
    return user;
  }

  throw new Error(
    'LandTrust+ payment succeeded but membership is still activating. Please wait a moment and try again.'
  );
};

/**
 * Creates an LT+ subscription and confirms the first payment inline.
 * Uses the same Stripe payment method / Payment Element as booking checkout.
 */
export const createAndConfirmLtPlusSubscription = async ({
  stripe,
  elements,
  billingInterval = LT_PLUS_BILLING_INTERVAL,
  paymentMethod,
  stripePaymentMethodId,
  billingDetails,
  onBeforeConfirm,
}) => {
  if (!stripe) {
    throw new Error('Payment system not ready. Please try again.');
  }

  const usesSavedCard = paymentMethod === 'defaultCard' && stripePaymentMethodId;

  if (!usesSavedCard) {
    if (!elements) {
      throw new Error('Payment system not ready. Please try again.');
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(submitError.message || 'Please check your payment details.');
    }
  }

  let subscriptionResponse;
  try {
    subscriptionResponse = await stripeCreateSubscription({
      billingInterval,
      originSection: 'checkout',
    });
  } catch (error) {
    if (typeof error?.message === 'string') {
      const parsedError = parseSubscriptionApiError(error.message);

      if (isLtPlusSubscriptionExistsError(parsedError)) {
        return { existingSubscription: true };
      }

      throw parsedError;
    }

    throw error;
  }

  const { clientSecret, subscriptionId } = subscriptionResponse || {};
  if (!clientSecret) {
    throw new Error('Unable to start LandTrust+ subscription.');
  }

  // The subscription exists but its payment intent is still `incomplete`; confirming it
  // (charging the membership) is a separate, slower Stripe step, so surface it in the UI.
  if (typeof onBeforeConfirm === 'function') {
    onBeforeConfirm();
  }

  const returnUrl = window.location.href;

  const confirmPaymentOptions = usesSavedCard
    ? {
        clientSecret,
        confirmParams: {
          payment_method: stripePaymentMethodId,
          return_url: returnUrl,
        },
        redirect: 'if_required',
      }
    : {
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnUrl,
          ...(billingDetails && Object.keys(billingDetails).length
            ? { payment_method_data: { billing_details: billingDetails } }
            : {}),
        },
        redirect: 'if_required',
      };

  const { error: confirmError } = await stripe.confirmPayment(confirmPaymentOptions);

  if (confirmError) {
    throw new Error(confirmError.message || 'LandTrust+ payment failed. Please try again.');
  }

  return { existingSubscription: false, subscriptionId };
};
